/**
 * Оценка квеста звёздами.
 *
 * Показ и выставление — один компонент: рисуются те же пять звёзд, разница
 * только в том, реагируют ли они на курсор. Так оценка не «переезжает» в
 * другое место экрана после того, как её поставили.
 *
 * Дробный балл закрашивается дробно: 4.3 — это четыре звезды и треть пятой.
 * Округлять до половины нельзя — средняя оценка тогда прыгает от одного
 * нового голоса, хотя изменилась на сотые.
 */
import { useState } from "react";

const STARS = [1, 2, 3, 4, 5];

export function StarRating({
  value,
  size = 16,
  onRate,
  title,
}: {
  value: number;
  size?: number;
  /** Задан — звёзды кликабельны. */
  onRate?: (rating: number) => void;
  title?: string;
}) {
  const [hovered, setHovered] = useState(0);

  const shown = hovered || value;
  // Ширина закрашенного слоя в процентах: он лежит поверх серого и обрезается.
  const filled = Math.max(0, Math.min(5, shown)) * 20;

  const stars = (className: string) =>
    STARS.map((star) => <Star key={star} size={size} className={className} />);

  if (!onRate) {
    return (
      <span className="relative inline-flex shrink-0 align-middle" title={title} aria-hidden>
        <span className="flex text-ink-600">{stars("")}</span>
        <span
          className="absolute inset-y-0 left-0 flex overflow-hidden text-amber-400"
          style={{ width: `${filled}%` }}
        >
          {stars("")}
        </span>
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 align-middle" onMouseLeave={() => setHovered(0)}>
      {STARS.map((star) => (
        <button
          key={star}
          type="button"
          className={`p-0.5 transition-transform hover:scale-110 ${
            star <= shown ? "text-amber-400" : "text-ink-600"
          }`}
          onMouseEnter={() => setHovered(star)}
          // Повторный клик по своей же оценке снимает её: передумать так же
          // просто, как поставить.
          onClick={() => onRate(star === value ? 0 : star)}
          aria-label={String(star)}
        >
          <Star size={size} className="" />
        </button>
      ))}
    </span>
  );
}

function Star({ size, className }: { size: number; className: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={`shrink-0 ${className}`}
      aria-hidden
    >
      <path d="M12 2.6l2.9 5.9 6.5.95-4.7 4.6 1.1 6.45L12 17.45 6.2 20.5l1.1-6.45-4.7-4.6 6.5-.95z" />
    </svg>
  );
}
